import {
  Badge,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  cn,
  EditorButton,
  type FileUploadData,
  fileExtensionFromMimeType,
  formatFileSize,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  parseFilename,
  Separator,
  useElementFromRef,
} from "@qw/ui";
import { type Editor, useCurrentEditor, useEditorState } from "@tiptap/react";
import { type FC, type RefObject, useCallback, useState } from "react";

interface EditorWorkflowFilesDropdownMenuProps {
  containerRef?: RefObject<HTMLDivElement | null>;
  files: FileUploadData[];
  nodeName: string;
  insertCommand: (editor: Editor, fileUploadId: string) => void;
}

export const EditorWorkflowFilesDropdownMenu: FC<EditorWorkflowFilesDropdownMenuProps> = ({
  containerRef,
  files,
  nodeName,
  insertCommand,
}) => {
  const container = useElementFromRef(containerRef);
  const [isOpen, setIsOpen] = useState(false);
  const { editor } = useCurrentEditor();

  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      const selectedOption = files.find((file) => ctx.editor?.isActive(nodeName, { fileUploadId: file.id }));
      const defaultValue = selectedOption ? parseFilename(selectedOption.filename).name : undefined;
      return { selectedOption, defaultValue };
    },
  });

  const handleSelect = useCallback(
    (id: string) => {
      if (editor) {
        insertCommand(editor, id);
      }
    },
    [editor, insertCommand]
  );

  const handleCloseAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
  }, []);

  if (!files?.length) {
    return null;
  }

  if (!editor) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <EditorButton icon="article" tooltip="Insert file" className="max-w-40 pr-2">
          <Icon icon="keyboard_arrow_down" className="size-4" />
        </EditorButton>
      </PopoverTrigger>

      {/* NOTE: We need to conditionally render the dropdown menu content to ensure the dropdown menu is properly
      positioned with respect to the container ref when the component is mounted or refreshed. */}
      {isOpen && (
        <PopoverContent
          align="start"
          onCloseAutoFocus={handleCloseAutoFocus}
          container={container}
          className="w-auto p-0"
        >
          <Command defaultValue={editorState?.defaultValue}>
            <div className="p-1">
              <CommandInput placeholder="Search..." />
            </div>

            {!!files?.length && (
              <>
                <Separator />
                <CommandList defaultValue={editorState?.defaultValue} className="max-h-88 max-w-88 p-1">
                  <CommandEmpty>No options found.</CommandEmpty>

                  {files.map((file) => {
                    const isSelected = file.id === editorState?.selectedOption?.id;
                    const name = parseFilename(file.filename).name;
                    const extension = fileExtensionFromMimeType(file.mimeType);
                    const size = formatFileSize(file.size ?? 0);

                    return (
                      <CommandItem key={file.id} value={name} onSelect={() => handleSelect(file.id)}>
                        <Icon icon="check" className={cn("flex-none size-4 text-base", { "opacity-0": !isSelected })} />
                        <div className="flex flex-auto flex-col min-w-0 max-w-full gap-0.5 py-1">
                          <span className="flex items-center gap-1.5 text-sm">
                            {name}{" "}
                            <Badge variant="secondary" size="2xs" type="square" className="uppercase">
                              {extension}
                            </Badge>
                          </span>
                          <span className="text-xs text-muted-foreground">{size}</span>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandList>
              </>
            )}
          </Command>
        </PopoverContent>
      )}
    </Popover>
  );
};

// Backwards compatibility wrapper
interface PlaybookEditorWorkflowFilesDropdownMenuProps {
  containerRef?: RefObject<HTMLDivElement | null>;
  files: FileUploadData[];
}

export const PlaybookEditorWorkflowFilesDropdownMenu: FC<PlaybookEditorWorkflowFilesDropdownMenuProps> = (props) => {
  return (
    <EditorWorkflowFilesDropdownMenu
      {...props}
      nodeName="playbookFile"
      insertCommand={(editor, id) => editor.chain().focus().insertPlaybookFile(id).run()}
    />
  );
};
